import {
  FocusedInput,
  OnDatesChangeProps,
  START_DATE,
  useDatepicker,
} from '@datepicker-react/hooks'
import { useId, useState } from 'react'
import { useMedia, XGroup, XStack } from 'tamagui'
import { usePopoverState } from '@tamagui-extras/core'
import { DatePickerProvider } from './date-picker-context'
import { FormFieldContainer } from '../form-field-container'
import { DatePickerFieldProps } from './date-picker.props'
import { DatePickerPopover } from './date-picker-popover'
import { getLocaleDate } from './helpers'
import { StyledInput } from '../styled-input'

export function DatePickerField({
  numberOfMonths,
  isRangePicker,
  onChange,
  labelFunctions,
  startDate = null,
  endDate = null,
  required,
  placeholder,
  error,
  helperText,
  label,
  hideLabel,
  labelProps,
  labelInline,
  fullWidth,
  inputProps,
  buttonProps,
  popoverProps,
  containerProps,
  showHelperText,
  isDirty,
}: DatePickerFieldProps) {
  const id = useId()
  const media = useMedia()
  const popoverState = usePopoverState(false)
  const [state, setState] = useState<OnDatesChangeProps>({
    startDate: startDate,
    endDate: endDate,
    focusedInput: START_DATE,
  })
  const monthsCount: number = isRangePicker ? (media.xs ? 1 : numberOfMonths ?? 2) : 1

  const { activeMonths, firstDayOfWeek, ...context } = useDatepicker({
    startDate: state.startDate,
    endDate: state.endDate,
    focusedInput: state.focusedInput as FocusedInput,
    onDatesChange: (data) => {
      if (!data.focusedInput) {
        setState({ ...data, focusedInput: START_DATE })
      } else {
        setState(data)
      }
      if (typeof onChange === 'function') {
        onChange(data)
      }
      if (!isRangePicker) {
        popoverState.onOpenChange(false)
      }
    },
    numberOfMonths: monthsCount,
    ...(!isRangePicker && {
      minBookingDays: 1,
      exactMinBookingDays: true,
    }),
  })

  const getInputValue = isRangePicker
    ? `${getLocaleDate({ date: state.startDate })}${
        state.endDate ? ' - ' + getLocaleDate({ date: state.endDate }) : ''
      }`
    : getLocaleDate({ date: state.startDate })

  const currentInputProps = {
    ...inputProps,
  }

  const currentButtonProps = {
    ...buttonProps,
  }

  return (
    <DatePickerProvider {...context}>
      <FormFieldContainer
        id={id}
        error={error}
        required={required}
        labelProps={labelProps}
        label={label}
        labelInline={labelInline}
        helperText={helperText}
        showHelperText={showHelperText}
        fullWidth={fullWidth}
        hideLabel={hideLabel}
        isDirty={isDirty}
        {...containerProps}
      >
        <XStack
          space
          flexGrow={1}
          flexShrink={1}
        >
          <XGroup flexGrow={fullWidth ? 1 : undefined} flexShrink={1} alignItems={'center'} flexDirection={'row'}>
            <XGroup.Item>
              <StyledInput
                width={'100%'}
                minWidth={isRangePicker ? '$16' : undefined}
                value={getInputValue}
                onPressIn={() => popoverState.onOpenChange(true)}
                borderRightWidth={0}
                focusStyle={{
                  outlineWidth: 0,
                }}
                {...currentInputProps}
                placeholder={placeholder}
              />
            </XGroup.Item>
            <XGroup.Item>
              <DatePickerPopover
                activeMonths={activeMonths}
                monthsCount={monthsCount}
                firstDayOfWeek={firstDayOfWeek}
                labelFunctions={labelFunctions}
                buttonProps={currentButtonProps}
                {...popoverState}
                {...popoverProps}
              />
            </XGroup.Item>
          </XGroup>
        </XStack>
      </FormFieldContainer>
    </DatePickerProvider>
  )
}
